import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";

export interface ServiceCardProps {
  id?: number | string;
  title: string;
  body?: string;
  description?: string;
  image?: string;
  slug?: string;
  category?: { name: string }[] | any;
  technologies?: { name: string; get_logo_url?: string }[];
  className?: string;
  isWrappedInLink?: boolean;
}

const DEFAULT_SERVICE_IMAGE =
  "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80";

const ServiceCard: React.FC<ServiceCardProps> = ({
  id,
  title,
  body,
  description,
  image,
  slug,
  category,
  technologies = [],
  className = "",
  isWrappedInLink = false,
}) => {
  const rawSnippet = body || description || "";
  const contentSnippet =
    typeof rawSnippet === "string"
      ? rawSnippet.replace(/<[^>]*>?/gm, "").trim()
      : "";

  const coverImage = image || DEFAULT_SERVICE_IMAGE;

  const primaryCategory =
    Array.isArray(category) && category.length > 0
      ? typeof category[0] === "string"
        ? category[0]
        : category[0]?.name
      : typeof category === "string"
        ? category
        : "Solution";

  const targetLink = slug && !isWrappedInLink ? `/services/${slug}` : undefined;

  const CardInner = (
    <article
      className={`cht-group cht-relative cht-flex cht-flex-col cht-h-full cht-w-full cht-bg-white cht-rounded-[20px] cht-border cht-border-slate-200/80 cht-shadow-soft hover:cht-shadow-[0_22px_45px_rgba(62,87,218,0.12)] hover:cht-border-[#3e57da]/40 hover:-cht-translate-y-1.5 cht-transition-all cht-duration-300 cht-overflow-hidden ${className}`}
    >
      {/* 16:9 Aspect Ratio Media Showcase Container */}
      <div className="cht-relative cht-aspect-[16/9] cht-w-full cht-overflow-hidden cht-bg-slate-100">
        <img
          src={coverImage}
          alt={title}
          loading="lazy"
          className="cht-w-full cht-h-full cht-object-cover group-hover:cht-scale-105 cht-transition-transform cht-duration-500"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src = DEFAULT_SERVICE_IMAGE;
          }}
        />

        {/* Ambient Dark Vignette Gradient */}
        <div className="cht-absolute cht-inset-0 cht-bg-gradient-to-t cht-from-black/60 cht-via-transparent cht-to-black/10" />
      </div>

      {/* Card Content Body */}
      <div className="cht-p-5 sm:cht-p-6 cht-flex cht-flex-col cht-flex-1 cht-space-y-3">
        {/* Title */}
        <h4 className="!cht-font-heading cht-text-lg sm:cht-text-xl !cht-font-extrabold cht-text-[#1e293b] group-hover:cht-text-[#3e57da] cht-transition-colors cht-line-clamp-2 !cht-leading-snug">
          {title}
        </h4>

        {/* Description Snippet */}
        <p className="cht-text-slate-600 cht-text-sm !cht-leading-relaxed cht-line-clamp-3 cht-flex-1">
          {contentSnippet ||
            "Professional engineering and technology solution tailored to your operational goals."}
        </p>

        {/* Card Action Footer */}
        <div className="cht-pt-3.5 cht-border-t cht-border-slate-100 cht-flex cht-items-center cht-justify-between cht-mt-auto">
          <span className="cht-text-xs cht-font-bold cht-text-[#3e57da] cht-inline-flex cht-items-center cht-gap-1.5">
            Explore Service
            <ArrowRight
              size={14}
              className="group-hover:cht-translate-x-1.5 cht-transition-transform cht-duration-300"
            />
          </span>
        </div>
      </div>
    </article>
  );

  if (targetLink) {
    return (
      <Link
        to={targetLink}
        className="cht-block cht-h-full cht-w-full cht-no-underline"
      >
        {CardInner}
      </Link>
    );
  }

  return CardInner;
};

export default ServiceCard;
